import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { Badge } from '@/ui/base/badge';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { useCicloFinanceiro } from './hooks/useCiclo';
import { useCicloFinanceiroPipeline } from './hooks/useFechamentoV2';
import { PipelineFinanceiro } from './PipelineFinanceiro';
import type { EtapaCiclo, MedicaoCicloLinha } from './types';

const ETAPAS: { valor: EtapaCiclo; titulo: string; subtitulo: string }[] = [
  {
    valor: 'CADASTRO',
    titulo: 'Cadastro',
    subtitulo: 'Medições com documentos de liquidação a cadastrar antes do empenho.',
  },
  {
    valor: 'EMPENHO',
    titulo: 'Empenho',
    subtitulo: 'Medições aprovadas aguardando registro de empenho no SOF.',
  },
  {
    valor: 'ATESTE',
    titulo: 'Ateste',
    subtitulo: 'Empenhos pendentes de ateste do recebimento do serviço.',
  },
  {
    valor: 'LIQUIDACAO',
    titulo: 'Liquidação',
    subtitulo: 'Medições prontas para liquidação contábil (NF/recibo validados).',
  },
  {
    valor: 'AUTORIZACAO',
    titulo: 'Autorização',
    subtitulo: 'Medições liquidadas aguardando autorização de pagamento (SEI).',
  },
];

type LinhaTabela = MedicaoCicloLinha & { id: string };

function formatarMesLongo(mes: string): string {
  return new Date(`${mes}-01T00:00:00`).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
}

const colunas: DataTableColumn<LinhaTabela>[] = [
  {
    header: 'Credenciado',
    sortKey: 'credenciadoNome',
    cell: (linha) => (
      <div>
        <p className="font-medium">{linha.credenciadoNome}</p>
        <p className="text-xs text-muted-foreground">
          {linha.natureza === 'PF' ? 'Pessoa Física' : 'PJ / Cooperativa'}
        </p>
      </div>
    ),
  },
  {
    header: 'Competência',
    cell: (linha) =>
      new Date(linha.mesReferencia).toLocaleDateString('pt-BR', {
        month: '2-digit',
        year: 'numeric',
      }),
  },
  { header: 'Valor', cell: (linha) => <MoneyDisplay value={linha.valorTotal} /> },
  { header: 'Status', cell: (linha) => <Badge variant="secondary">{linha.status}</Badge> },
  {
    header: 'Ações',
    headerClassName: 'text-right',
    cellClassName: 'text-right',
    cell: (linha) => (
      <Button asChild size="sm" variant="ghost">
        <Link
          to={`/operacao/financeiro/fechamento-mensal/${linha.mesReferencia}__${linha.natureza}?aba=pos-fechamento`}
        >
          Abrir competência
          <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
        </Link>
      </Button>
    ),
  },
];

/** FIN-012: pipeline financeiro — Cadastro → Empenho → Ateste → Liquidação → Autorização. */
export function CicloFinanceiroPage() {
  const [parametros, definirParametros] = useSearchParams();
  const [page, definirPage] = useState(1);
  const [natureza, definirNatureza] = useState<'ALL' | 'PF' | 'PJ'>('ALL');
  const etapa = (parametros.get('etapa') as EtapaCiclo | null) ?? 'CADASTRO';
  const mes = parametros.get('mes') ?? '';
  const perPage = 20;

  const etapaAtual = ETAPAS.find((item) => item.valor === etapa) ?? ETAPAS[0]!;

  const { data, isLoading, isError, refetch } = useCicloFinanceiro({
    etapa,
    mes: mes || undefined,
    natureza: natureza === 'ALL' ? undefined : natureza,
    page,
    perPage,
  });
  const pipeline = useCicloFinanceiroPipeline(mes);
  const linhas: LinhaTabela[] = (data?.dados ?? []).map((linha) => ({ ...linha, id: linha.id }));

  function mudarEtapa(novaEtapa: string) {
    definirPage(1);
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('etapa', novaEtapa);
      return proximo;
    });
  }

  function mudarMes(novoMes: string) {
    definirPage(1);
    definirParametros((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('mes', novoMes);
      return proximo;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Financeiro"
        title="Ciclo financeiro"
        description="Acompanhe o ciclo orçamentário-financeiro multi-competência por etapa: Cadastro, Empenho, Ateste, Liquidação e Autorização."
      />

      <Tabs value={etapa} onValueChange={mudarEtapa}>
        <TabsList>
          {ETAPAS.map((item) => (
            <TabsTrigger key={item.valor} value={item.valor}>
              {item.titulo}
            </TabsTrigger>
          ))}
        </TabsList>

        <Card className="mt-4 rounded-2xl">
          <CardContent className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-tight">{etapaAtual.titulo}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{etapaAtual.subtitulo}</p>
            </div>
            <div className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
              <MonthFilter label="Competência" value={mes} onChange={mudarMes} />
              <div className="space-y-1">
                <Label htmlFor="ciclo-natureza">Natureza</Label>
                <Select
                  value={natureza}
                  onValueChange={(v) => definirNatureza(v as 'ALL' | 'PF' | 'PJ')}
                >
                  <SelectTrigger id="ciclo-natureza" className="w-full sm:w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todas</SelectItem>
                    <SelectItem value="PF">PF</SelectItem>
                    <SelectItem value="PJ">PJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {pipeline.data && (
          <div className="mt-4">
            <PipelineFinanceiro
              dados={pipeline.data}
              titulo={`Visão geral — ${formatarMesLongo(mes)}`}
            />
          </div>
        )}

        {ETAPAS.map((item) => (
          <TabsContent key={item.valor} value={item.valor} className="mt-4">
            {isError ? (
              <ErrorState
                variant="load-failed"
                primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
              />
            ) : (
              <DataTable
                columns={colunas}
                data={linhas}
                isLoading={isLoading}
                total={data?.total}
                page={page}
                perPage={perPage}
                onPageChange={definirPage}
                emptyTitle="Nenhuma medição nesta etapa"
                emptyDescription="Não há medição no seu escopo para esta etapa do ciclo."
                ariaLabel={`Medições — ${item.titulo}`}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default CicloFinanceiroPage;
